import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, LogOut, Power } from "lucide-react";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { CouponsPanel } from "@/components/admin/CouponsPanel";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { AdminsPanel } from "@/components/admin/AdminsPanel";
import { GuidesPanel } from "@/components/admin/GuidesPanel";
import { PricingPanel } from "@/components/admin/PricingPanel";
import { FeedbackLogPanel } from "@/components/admin/FeedbackLogPanel";
import { LegalPanel } from "@/components/admin/LegalPanel";
import { TestimonialsPanel } from "@/components/admin/TestimonialsPanel";
import { ForcePasswordChange } from "@/components/admin/ForcePasswordChange";
import {
  adminMe,
  adminLogout,
  getRubric,
  saveRubric,
  changePassword,
  get2faSetup,
  enable2fa,
  disable2fa,
  getAnalytics,
  getAiEnabled,
  setAiEnabled as apiSetAiEnabled,
  getCoupons,
  createCoupon,
  deleteCoupon,
  reinstateCoupon,
  getAdmins,
  createAdmin,
  deleteAdmin,
  clearToken,
  getToken,
} from "@/lib/api";

export default function Admin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  const [rubric, setRubric] = useState("");
  const [savingRubric, setSavingRubric] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [aiOn, setAiOn] = useState(true);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");

  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: 10, expires_at: "" });
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: "", role: "editor" });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  const isOwner = me?.role === "owner";

  const logout = useCallback(async () => {
    await adminLogout();
    clearToken();
    navigate("/admin-login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadAll = useCallback(async () => {
    const user = await adminMe();
    setMe(user);
    if (user.must_change_password) {
      setChecking(false);
      return;
    }
    if (user.role === "owner") {
      const [a, r, ai, cp, ad] = await Promise.all([
        getAnalytics(), getRubric(), getAiEnabled(), getCoupons(), getAdmins(),
      ]);
      setAnalytics(a);
      setRubric(r.rubric);
      setAiOn(ai.enabled);
      setCoupons(cp.coupons);
      setAdmins(ad.admins);
    }
    setChecking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin-login");
      return;
    }
    loadAll().catch(() => {
      toast.error("Session expired. Please sign in.");
      logout();
    });
  }, [navigate, logout, loadAll]);

  const toggleAi = async (next) => {
    setAiOn(next);
    try {
      await apiSetAiEnabled(next);
      toast.success(next ? "AI tool is ON." : "AI tool is OFF — no credits will be used.");
    } catch {
      setAiOn(!next);
      toast.error("Couldn't update the toggle.");
    }
  };

  const onSaveRubric = async () => {
    setSavingRubric(true);
    try {
      await saveRubric(rubric);
      toast.success("Rubric saved. It applies to the next submission immediately.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save rubric.");
    } finally {
      setSavingRubric(false);
    }
  };

  const onChangePw = async () => {
    setSavingPw(true);
    try {
      await changePassword(curPw, newPw);
      toast.success("Password changed.");
      setCurPw("");
      setNewPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change password.");
    } finally {
      setSavingPw(false);
    }
  };

  const onStartSetup = async () => {
    try {
      setSetup(await get2faSetup());
    } catch {
      toast.error("Couldn't start 2FA setup.");
    }
  };

  const onEnable2fa = async () => {
    try {
      await enable2fa(code);
      toast.success("Two-factor authentication enabled.");
      setMe((m) => ({ ...m, totp_enabled: true }));
      setSetup(null);
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code.");
    }
  };

  const onDisable2fa = async () => {
    try {
      await disable2fa(code);
      toast.success("Two-factor authentication disabled.");
      setMe((m) => ({ ...m, totp_enabled: false }));
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code.");
    }
  };

  const refreshCoupons = async () => {
    try {
      setCoupons((await getCoupons()).coupons);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Couldn't refresh coupons:", e);
    }
  };

  const onCreateCoupon = async () => {
    setCreatingCoupon(true);
    try {
      await createCoupon({
        code: newCoupon.code.trim().toUpperCase(),
        discount_percent: Number(newCoupon.discount_percent),
        expires_at: newCoupon.expires_at ? new Date(newCoupon.expires_at).toISOString() : "",
      });
      toast.success("Coupon created.");
      setNewCoupon({ code: "", discount_percent: 10, expires_at: "" });
      await refreshCoupons();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create coupon.");
    } finally {
      setCreatingCoupon(false);
    }
  };

  const onDeleteCoupon = async (c) => {
    try {
      await deleteCoupon(c);
      toast.success("Coupon removed.");
      await refreshCoupons();
    } catch {
      toast.error("Couldn't remove coupon.");
    }
  };

  const onReinstateCoupon = async (c) => {
    try {
      await reinstateCoupon(c);
      toast.success("Coupon reinstated.");
      await refreshCoupons();
    } catch {
      toast.error("Couldn't reinstate coupon.");
    }
  };

  const refreshAdmins = async () => {
    try {
      setAdmins((await getAdmins()).admins);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Couldn't refresh admins:", e);
    }
  };

  const onCreateAdmin = async () => {
    setCreatingAdmin(true);
    setTempPassword(null);
    try {
      const res = await createAdmin({ email: newAdmin.email.trim().toLowerCase(), role: newAdmin.role });
      setTempPassword(res.temporary_password);
      toast.success("Admin created.");
      setNewAdmin({ email: "", role: "editor" });
      await refreshAdmins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create admin.");
    } finally {
      setCreatingAdmin(false);
    }
  };

  const onDeleteAdmin = async (email) => {
    try {
      await deleteAdmin(email);
      toast.success("Admin removed.");
      await refreshAdmins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove admin.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-sand">
        <Loader2 className="h-8 w-8 animate-spin text-clay" />
      </div>
    );
  }

  if (me?.must_change_password) {
    return (
      <ForcePasswordChange
        email={me.email}
        onLogout={logout}
        onDone={() => {
          setMe((m) => ({ ...m, must_change_password: false }));
          setChecking(true);
          loadAll().catch(() => logout());
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <header className="border-b border-border/70 bg-sand/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${isOwner ? "text-clay bg-clay/10" : "text-forest bg-forest/10"}`} data-testid="admin-role-badge">
              {me?.role}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-ink-muted hidden sm:block">{me?.email}</span>
            <Button variant="ghost" data-testid="admin-logout-button" onClick={logout} className="text-ink-muted hover:text-ink">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
        {isOwner && (
          <div className="mb-8 bg-white border border-border rounded-2xl p-6 flex items-center justify-between gap-4" data-testid="ai-toggle-card">
            <div className="flex items-center gap-3">
              <div className={`grid h-10 w-10 place-items-center rounded-xl ${aiOn ? "bg-forest/10 text-forest" : "bg-clay/10 text-clay"}`}>
                <Power className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <div className="font-heading text-lg text-ink">AI feedback tool</div>
                <p className="text-sm text-ink-muted">
                  {aiOn ? "On — students can submit and the AI is used." : "Off — no credits are used."}
                </p>
              </div>
            </div>
            <Switch checked={aiOn} onCheckedChange={toggleAi} data-testid="ai-toggle-switch" />
          </div>
        )}

        <Tabs defaultValue={isOwner ? "analytics" : "guides"}>
          <TabsList className="bg-sand-deep flex-wrap h-auto">
            {isOwner && <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>}
            <TabsTrigger value="guides" data-testid="tab-guides">Guides</TabsTrigger>
            {isOwner && <TabsTrigger value="rubric" data-testid="tab-rubric">Marking rubric</TabsTrigger>}
            {isOwner && <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing &amp; sales</TabsTrigger>}
            {isOwner && <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>}
            {isOwner && <TabsTrigger value="feedbacklog" data-testid="tab-feedbacklog">Feedback log</TabsTrigger>}
            {isOwner && <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>}
            {isOwner && <TabsTrigger value="legal" data-testid="tab-legal">Legal pages</TabsTrigger>}
            {isOwner && <TabsTrigger value="admins" data-testid="tab-admins">Admins</TabsTrigger>}
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {isOwner && <AnalyticsPanel analytics={analytics} />}

          <GuidesPanel />

          {isOwner && (
            <TabsContent value="rubric" className="mt-6">
              <div className="bg-white border border-border rounded-2xl p-8">
                <h2 className="font-heading text-xl text-ink">AI marking rubric</h2>
                <p className="mt-2 text-sm text-ink-muted max-w-2xl">
                  This is the system prompt the AI uses to mark every statement.
                  Edits take effect immediately for the next submission.
                </p>
                <textarea
                  data-testid="rubric-textarea"
                  value={rubric}
                  onChange={(e) => setRubric(e.target.value)}
                  className="mt-5 w-full bg-sand border border-border focus:border-clay focus:ring-1 focus:ring-clay rounded-xl p-5 text-sm font-mono leading-relaxed resize-y min-h-[420px] outline-none"
                />
                <div className="mt-5 flex justify-end">
                  <Button data-testid="save-rubric-button" onClick={onSaveRubric} disabled={savingRubric} className="bg-clay hover:bg-clay-hover text-white rounded-lg px-7 py-5 font-medium">
                    {savingRubric ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save rubric</>)}
                  </Button>
                </div>
              </div>
            </TabsContent>
          )}

          {isOwner && <PricingPanel />}

          {isOwner && (
            <CouponsPanel
              coupons={coupons}
              newCoupon={newCoupon}
              setNewCoupon={setNewCoupon}
              creatingCoupon={creatingCoupon}
              onCreateCoupon={onCreateCoupon}
              onDeleteCoupon={onDeleteCoupon}
              onReinstateCoupon={onReinstateCoupon}
            />
          )}

          {isOwner && <FeedbackLogPanel />}

          {isOwner && <TestimonialsPanel />}

          {isOwner && <LegalPanel />}

          {isOwner && (
            <AdminsPanel
              admins={admins}
              currentEmail={me?.email}
              newAdmin={newAdmin}
              setNewAdmin={setNewAdmin}
              creating={creatingAdmin}
              onCreate={onCreateAdmin}
              onDelete={onDeleteAdmin}
              tempPassword={tempPassword}
            />
          )}

          <SettingsPanel
            me={me}
            curPw={curPw}
            setCurPw={setCurPw}
            newPw={newPw}
            setNewPw={setNewPw}
            savingPw={savingPw}
            onChangePw={onChangePw}
            setup={setup}
            code={code}
            setCode={setCode}
            onStartSetup={onStartSetup}
            onEnable2fa={onEnable2fa}
            onDisable2fa={onDisable2fa}
          />
        </Tabs>
      </main>
    </div>
  );
}
